import { useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { resolveRuntimeQrOptions, type RuntimeQrRequest } from './qrRuntime';

export function StyledQrCode(request: RuntimeQrRequest) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);
  const options = resolveRuntimeQrOptions(request);
  const {
    value,
    size,
    width,
    height,
    icon,
    iconSize,
    hideBackgroundDots,
    centerLabel,
    centerLabelWidth,
    centerLabelHeight,
    centerLabelFontSize,
    centerLabelForeground,
    centerLabelBackground,
    shape,
    style,
    foreground,
    background,
    margin,
  } = options;
  const renderedImage = centerLabel
    ? createCenterLabelDataUri({
        label: centerLabel,
        width: centerLabelWidth,
        height: centerLabelHeight,
        fontSize: centerLabelFontSize,
        foreground: centerLabelForeground,
        background: centerLabelBackground,
      })
    : icon;
  const imageSize = centerLabel
    ? Math.min(0.5, centerLabelWidth / Math.min(width, height))
    : Math.min(0.5, iconSize / Math.min(width, height));
  const imageMargin = centerLabel ? 0 : 3;
  const errorCorrectionLevel = renderedImage ? 'H' as const : 'Q' as const;

  useEffect(() => {
    if (!containerRef.current || !value) return;

    const nextOptions = {
      width,
      height,
      type: 'svg' as const,
      shape,
      margin,
      data: value,
      image: renderedImage,
      qrOptions: {
        errorCorrectionLevel,
      },
      dotsOptions: {
        color: foreground,
        type: style,
      },
      cornersSquareOptions: {
        color: foreground,
        type: 'extra-rounded' as const,
      },
      cornersDotOptions: {
        color: foreground,
        type: 'dot' as const,
      },
      backgroundOptions: {
        color: background,
      },
      imageOptions: {
        hideBackgroundDots,
        imageSize,
        margin: imageMargin,
      },
    };

    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(nextOptions);
      QRCodeStyling._clearContainer(containerRef.current);
      qrRef.current.append(containerRef.current);
      return;
    }

    qrRef.current.update(nextOptions);
  }, [background, foreground, height, hideBackgroundDots, imageMargin, imageSize, margin, renderedImage, shape, size, style, value, width]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        display: 'grid',
        placeItems: 'center',
      }}
    />
  );
}

function createCenterLabelDataUri(params: {
  label: string;
  width: number;
  height: number;
  fontSize: number;
  foreground: string;
  background: string;
}): string {
  const radius = Math.round(params.height / 2);
  const escapedLabel = escapeSvg(params.label);
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${params.width}" height="${params.height}" viewBox="0 0 ${params.width} ${params.height}">`,
    `<rect x="0" y="0" width="${params.width}" height="${params.height}" rx="${radius}" fill="${params.background}"/>`,
    `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="${params.fontSize}" font-weight="800" fill="${params.foreground}">${escapedLabel}</text>`,
    '</svg>',
  ].join('');
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

function escapeSvg(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
